import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Settings, Camera, Plus, Grid3X3, Trash2, Edit2, X, ImageIcon, ZoomIn, ZoomOut, Play, Palmtree, Copy, Share2, MapPin, BadgeCheck
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";
import { AdeLoadingSpinner } from "@/components/AdeLoadingSpinner";
import ImageCropper from "@/components/ImageCropper";
import { getSceneImage } from "@/components/IslandScene";
import { shareIsland } from "@/utils/shareIsland";
import { GoldenRingAvatar } from "@/components/GoldenRingAvatar";
import { DiscussionGridTile, PostCard } from "@/components/PostCard";
import { LikesListModal, GiftsListModal } from "@/components/PhotoInteractionModals";
import { GiftDetailsModal } from "@/components/ProfileGiftDetails";
import { useLang } from "@/i18n";
import { CreatorLevelBadge } from "@/components/CreatorLevelBadge";
import LevelVault from "@/components/LevelVault";

export default function Profile() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [user, setUser] = useState(null);
  const [myDiscussions, setMyDiscussions] = useState([]);
  useEffect(() => {
    const load = () => api.get("/auth/me").then((r) =>
      api.get(`/discussions/user/${r.data.user_id}`).then((d) => setMyDiscussions(d.data.discussions))
    ).catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);
  const [photos, setPhotos] = useState([]);

  // ordine cronologico unico per post + discussion nella griglia
  const gridOrder = useMemo(() => {
    const items = [
      ...myDiscussions.map((d) => ({ id: `d_${d.id}`, ts: d.created_at || "" })),
      ...photos.map((p) => ({ id: `p_${p.photo_id}`, ts: p.created_at || "" })),
    ].sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
    const m = {};
    items.forEach((it, i) => { m[it.id] = i; });
    return m;
  }, [myDiscussions, photos]);
  const [connections, setConnections] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Photo upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showGiftsModal, setShowGiftsModal] = useState(false);
  const [showLevelVault, setShowLevelVault] = useState(false);
  const [newPhotoFile, setNewPhotoFile] = useState(null);
  const [newPhotoPreview, setNewPhotoPreview] = useState(null);
  const [newPhotoCaption, setNewPhotoCaption] = useState("");
  
  // Photo detail modal
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [postDetail, setPostDetail] = useState(null);
  const [detailLikes, setDetailLikes] = useState(null);
  const [detailGifts, setDetailGifts] = useState(null);

  useEffect(() => {
    if (!selectedPhoto) { setPostDetail(null); return; }
    api.get(`/photos/${selectedPhoto.photo_id}/detail`)
      .then((r) => setPostDetail(r.data.post))
      .catch(() => setPostDetail(null));
  }, [selectedPhoto]);

  const detailLike = async (photoId) => {
    try {
      const r = await api.post(`/photos/${photoId}/like`);
      setPostDetail((prev) => prev ? { ...prev, is_liked: r.data.liked, likes_count: r.data.likes_count } : prev);
    } catch (e) { /* noop */ }
  };

  const detailComment = async (photoId, content) => {
    try {
      const r = await api.post(`/photos/${photoId}/comments`, { content });
      setPostDetail((prev) => prev ? {
        ...prev,
        comments_count: (prev.comments_count || 0) + 1,
        recent_comments: [...(prev.recent_comments || []), {
          comment_id: r.data.comment_id, content, user: r.data.user,
        }].slice(-3),
      } : prev);
      return true;
    } catch (e) { return false; }
  };
  const [editingCaption, setEditingCaption] = useState(false);
  const [editedCaption, setEditedCaption] = useState("");
  
  // Avatar zoom modal
  const [showAvatarZoom, setShowAvatarZoom] = useState(false);
  const [avatarScale, setAvatarScale] = useState(1);
  const [heroParallax, setHeroParallax] = useState(0);
  useEffect(() => {
    const onScroll = () => setHeroParallax(Math.min(Math.max(window.scrollY, 0) * 0.35, 56));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  
  // Image cropper
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperType, setCropperType] = useState('avatar'); // 'avatar', 'cover', 'post'
  
  const profileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const photoInputRef = useRef(null);
  
  const fetchProfile = useCallback(async (showToast = false) => {
    try {
      const [meResponse, connectionsResponse] = await Promise.all([
        api.get('/auth/me'),
        api.get('/connections')
      ]);
      
      const profileResponse = await api.get(`/profile/${meResponse.data.user_id}`);
      setUser(profileResponse.data);
      setPhotos(profileResponse.data.photos || []);
      setConnections(connectionsResponse.data.connections || []);
      if (showToast) {
        toast.success("Profilo aggiornato!");
      }
    } catch (error) {
      toast.error("Errore nel caricamento del profilo");
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Refresh sound
  const playRefreshSound = () => {
    try {
      const sound = new Audio('/sounds/refresh.wav');
      sound.volume = 0.3;
      sound.play().catch(() => {});
    } catch (e) {}
  };
  
  // Manual refresh handler
  const handleManualRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    
    // Play refresh sound
    playRefreshSound();
    
    await fetchProfile(true);
    setTimeout(() => setIsRefreshing(false), 3000);
  }, [fetchProfile, isRefreshing]);
  
  useEffect(() => {
    fetchProfile(false);
  }, [fetchProfile]);
  
  // Handle file selection - open cropper
  const handleFileSelect = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 10MB)");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      setCropperImage(reader.result);
      setCropperType(type);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
    
    // Reset input
    e.target.value = '';
  };
  
  // Handle cropped image upload
  const handleCroppedImage = async (croppedBlob) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', croppedBlob, 'cropped-image.jpg');
      
      if (cropperType === 'avatar') {
        await api.post('/upload/profile-picture', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success("Foto profilo aggiornata");
      } else if (cropperType === 'cover') {
        await api.post('/upload/cover-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success("Copertina aggiornata");
      } else if (cropperType === 'post') {
        setNewPhotoFile(croppedBlob);
        setNewPhotoPreview(URL.createObjectURL(croppedBlob));
        setShowUploadModal(true);
        setUploading(false);
        return;
      }
      
      fetchProfile();
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };
  
  const handleProfileUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 5MB)");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/upload/profile-picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchProfile();
      toast.success("Foto profilo aggiornata");
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };
  
  const handleCoverUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 5MB)");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/upload/cover-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchProfile();
      toast.success("Copertina aggiornata");
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };
  
  // Open upload modal for new photo
  const handleNewPhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 5MB)");
      return;
    }
    
    setNewPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setNewPhotoPreview(e.target.result);
    reader.readAsDataURL(file);
    setShowUploadModal(true);
  };
  
  // Upload photo with caption
  const handlePhotoUpload = async () => {
    if (!newPhotoFile) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', newPhotoFile);
      if (newPhotoCaption.trim()) {
        formData.append('caption', newPhotoCaption.trim());
      }
      
      await api.post('/upload/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      fetchProfile();
      setShowUploadModal(false);
      setNewPhotoFile(null);
      setNewPhotoPreview(null);
      setNewPhotoCaption("");
      toast.success("Foto pubblicata!");
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };
  
  // Delete photo
  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Eliminare questa foto?")) return;
    
    try {
      await api.delete(`/photos/${photoId}`);
      setSelectedPhoto(null);
      fetchProfile();
      toast.success("Foto eliminata");
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };
  
  // Update photo caption
  const handleUpdateCaption = async () => {
    if (!selectedPhoto) return;
    
    try {
      await api.put(`/photos/${selectedPhoto.photo_id}`, {
        caption: editedCaption.trim()
      });
      
      setSelectedPhoto({ ...selectedPhoto, caption: editedCaption.trim() });
      setEditingCaption(false);
      fetchProfile();
      toast.success("Descrizione aggiornata");
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Oggi";
    if (diffDays === 1) return "Ieri";
    if (diffDays < 7) return `${diffDays}g fa`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}s fa`;
    return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <AdeLoadingSpinner size="lg" />
      </div>
    );
  }
  
  const getAvatarRingClass = () => {
    return user?.is_verified ? 'ring-2 ring-offset-2 ring-[#D4A574]' : '';
  };
  
  const getImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };
  
  return (
    <div className="page-content bg-white" data-testid="profile-page">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pt-4 safe-top">
        {/* ADE Logo Refresh Button - LEFT side */}
        <button
          onClick={handleManualRefresh}
          disabled={isRefreshing}
          className="focus:outline-none active:scale-95 transition-transform"
          title="Aggiorna"
          data-testid="ade-refresh-btn"
        >
          <img
            src="/images/ade-logo-extracted.png"
            alt="Aggiorna"
            className={`object-contain drop-shadow-lg transition-transform ${
              isRefreshing ? 'animate-spin-slow' : 'hover:scale-105'
            }`}
            style={{ width: '56px', height: '56px' }}
          />
        </button>
        <h1 className="flex-1 text-lg font-semibold text-gray-900">{user?.name}</h1>
        <button
          onClick={() => navigate('/app/settings')}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          data-testid="settings-btn"
        >
          <Settings className="w-5 h-5 text-gray-600" strokeWidth={1.5} />
        </button>
      </div>
      
      {/* Equipped island banner — hero cinematico con parallax */}
      <div className="profile-hero-cinematic relative h-48 mt-3" data-testid="profile-island-banner">
        <img src={getSceneImage(user?.island_type, false)} alt="" draggable={false}
             className="w-full h-full object-cover" style={{ objectPosition: "center 52%", transform: `translateY(${heroParallax}px) scale(1.14)`, willChange: "transform" }} />
        <div className="absolute inset-0 island-banner-fade" />
        {user?.island_name && (
          <div className="absolute top-3 right-3 flex items-center gap-1.5">
            <button
              onClick={async () => {
                try {
                  const r = await shareIsland(user.island_type, user.island_name);
                  if (r === "downloaded") toast.success("Immagine scaricata: condividila sui social!");
                } catch { toast.error("Condivisione non riuscita"); }
              }}
              className="w-7 h-7 rounded-full flex items-center justify-center active:scale-90 transition-transform"
              style={{ background: "rgba(8,12,24,0.5)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.15)" }}
              data-testid="share-island-chip"
            >
              <Share2 className="w-3.5 h-3.5 text-cyan-300" />
            </button>
            <button
              onClick={() => {
                const name = user.island_name;
              const done = () => toast.success("Nome isola copiato!");
              const fallback = () => {
                const ta = document.createElement("textarea");
                ta.value = name; document.body.appendChild(ta); ta.select();
                try { document.execCommand("copy"); done(); } catch {}
                document.body.removeChild(ta);
              };
              if (navigator.clipboard?.writeText) navigator.clipboard.writeText(name).then(done).catch(fallback);
              else fallback();
            }}
            className="px-2.5 py-1 rounded-full text-[10px] font-semibold text-white flex items-center gap-1 active:scale-95 transition-transform"
            style={{ background: "rgba(8,12,24,0.5)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.15)" }}
            data-testid="island-name-chip"
          >
            <Palmtree className="w-3 h-3 text-emerald-300" />
            {user.island_name}
            <Copy className="w-2.5 h-2.5 text-white/60 ml-0.5" />
          </button>
          </div>
        )}
      </div>

      {/* Profile Section */}
      <div className="px-4 relative z-10 -mt-10">
        <div className="flex items-start gap-6">
          {/* Avatar - with Golden Ring if applicable */}
          <div className="profile-avatar-hero relative flex-shrink-0">
            {user?.golden?.level > 0 ? (
              <div className="relative">
                <GoldenRingAvatar
                  user={user}
                  size="lg"
                  onClick={() => user?.picture && setShowAvatarZoom(true)}
                  className="cursor-pointer hover:opacity-90 transition-opacity"
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    profileInputRef.current?.click();
                  }}
                  className="absolute -bottom-1 -right-1 p-1.5 rounded-full bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 text-white shadow-lg z-30"
                  disabled={uploading}
                  data-testid="change-avatar-btn"
                >
                  <Camera className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <>
                <Avatar 
                  className={`w-20 h-20 cursor-pointer hover:opacity-90 transition-opacity`}
                  onClick={() => user?.picture && setShowAvatarZoom(true)}
                  data-testid="avatar-zoom-trigger"
                >
                  {user?.picture ? (
                    <AvatarImage 
                      src={getImageUrl(user.picture)} 
                      alt={user.name} 
                      className="object-cover"
                    />
                  ) : (
                    <AvatarFallback className="bg-gradient-to-br from-orange-100 via-pink-100 to-purple-100 text-purple-500 text-2xl font-medium">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  )}
                </Avatar>
                <button
                  onClick={() => profileInputRef.current?.click()}
                  className="absolute -bottom-1 -right-1 p-1.5 rounded-full bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 text-white shadow-lg"
                  disabled={uploading}
                  data-testid="change-avatar-btn"
                >
                  <Camera className="w-3 h-3" />
                </button>
              </>
            )}
            <input
              ref={profileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileSelect(e, 'avatar')}
              className="hidden"
            />
          </div>
          
          {/* Stats - Instagram style */}
          <div className="flex-1 flex justify-around pt-2">
            <div className="text-center" data-testid="post-count">
              <div className="text-xl font-semibold text-gray-900">{photos.length}</div>
              <div className="text-xs text-gray-500">{t('post')}</div>
            </div>
            <div 
              className="text-center cursor-pointer" 
              onClick={() => navigate('/app/connections')}
              data-testid="connections-count"
            >
              <div className="text-xl font-semibold text-gray-900">{connections.length}</div>
              <div className="text-xs text-gray-500">{t('connectionsTxt')}</div>
            </div>
            <div className="text-center" data-testid="likes-count">
              <div className="text-xl font-semibold text-gray-900">
                {photos.reduce((sum, p) => sum + (p.likes_count || 0), 0)}
              </div>
              <div className="text-xs text-gray-500">{t('adeLikes')}</div>
            </div>
            <div
              className="text-center cursor-pointer active:scale-95 transition-transform"
              data-testid="gifts-count"
              onClick={() => setShowGiftsModal(true)}
              title="Vedi regali ricevuti"
            >
              <div className="text-xl font-semibold" style={{ color: "#D4AF37" }}>
                {user?.total_gifts_received || 0}
              </div>
              <div className="text-xs font-semibold" style={{ color: "#D4AF37" }}>Regali</div>
              {(user?.total_gift_value || 0) > 0 && (
                <div className="text-[10px] text-amber-500 font-semibold">{(user.total_gift_value || 0).toLocaleString()} ADE</div>
              )}
            </div>
          </div>
        </div>
        
        {/* Name & Bio */}
        <div className="mt-4">
          <h2 className="text-sm font-semibold text-gray-900">
            {user?.name}
            {user?.is_verified && (
              <BadgeCheck className="inline w-4 h-4 ml-1 align-[-2px] text-[#D4A574]" strokeWidth={2.4} />
            )}
            <span className="ml-2 inline-block align-middle">
              <CreatorLevelBadge
                level={user?.creator_level
                  ? { level: user.creator_level.level, name: user.creator_level.name }
                  : { level: 1, name: "Livelli" }}
                locked={user?.creator_status !== "approved"}
                onClick={() => setShowLevelVault(true)}
              />
            </span>
          </h2>
          {user?.city && (
            <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
              <MapPin className="w-3 h-3" strokeWidth={2.2} />
              {user.city}
            </p>
          )}
          {user?.bio && (
            <p className="text-sm text-gray-700 mt-2 leading-relaxed">{user.bio}</p>
          )}
        </div>

        {/* Gift Details Modal — opened from the gold "Regali" counter */}
        {user?.user_id && (
          <GiftDetailsModal userId={user.user_id} open={showGiftsModal} onOpenChange={setShowGiftsModal} />
        )}
        
      </div>
      
      {/* Photo Grid Header */}
      <div className="mt-6 border-t border-gray-100">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2 text-gray-900">
            <Grid3X3 className="w-4 h-4" strokeWidth={2} />
            <span className="text-sm font-medium">{t('post')}</span>
          </div>
          <button
            onClick={() => photoInputRef.current?.click()}
            className="flex items-center gap-1 text-pink-500 hover:text-pink-600 transition-colors"
            disabled={uploading}
            data-testid="add-photo-btn"
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm font-medium">{t('addPhotoBtn')}</span>
          </button>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(e, 'post')}
            className="hidden"
          />
        </div>
        
        {/* Photo Grid — Discussions live in the same grid as Posts (ordine cronologico via CSS order) */}
        {(photos.length > 0 || myDiscussions.length > 0) ? (
          <div className="grid grid-cols-3 gap-0.5" data-testid="profile-discussions">
            {myDiscussions.map((d) => <DiscussionGridTile key={d.id} d={d} style={{ order: gridOrder[`d_${d.id}`] ?? 0 }} />)}
            {photos.map((photo) => (
              <div 
                key={photo.photo_id} 
                className="aspect-square bg-gray-100 relative group cursor-pointer"
                style={{ order: gridOrder[`p_${photo.photo_id}`] ?? 0 }}
                onClick={() => {
                  setSelectedPhoto(photo);
                  setEditedCaption(photo.caption || "");
                }}
                data-testid={`photo-${photo.photo_id}`}
              >
                {photo.media_type === 'video' ? (
                  <>
                    <video
                      src={`${getImageUrl(photo.image_url)}#t=0.001`}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                      data-testid={`grid-video-${photo.photo_id}`}
                    />
                    <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center pointer-events-none z-10">
                      <Play className="w-3 h-3 text-white" fill="white" />
                    </div>
                  </>
                ) : (
                  <img 
                    src={getImageUrl(photo.image_url)}
                    alt={photo.caption || ""}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                )}
                {/* Hover overlay with ADE logo */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex items-center gap-1.5 text-white text-sm font-medium">
                    <img src="/images/ade-logo-extracted.png" alt="" className="w-5 h-5 object-contain" />
                    <span>{photo.likes_count || 0}</span>
                  </div>
                </div>
                {/* Date badge */}
                <div className="absolute bottom-1 right-1 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded">
                  {formatDate(photo.created_at)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <ImageIcon className="w-12 h-12 mb-3 stroke-1" />
            <p className="text-sm">{t('noPostsYet')}</p>
            <button
              onClick={() => photoInputRef.current?.click()}
              className="mt-3 text-pink-500 text-sm font-medium hover:text-pink-600"
            >
              {t('publishFirstPhotoLink')}
            </button>
          </div>
        )}
      </div>
      
      {/* Photo Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("newPost")}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Preview */}
            {newPhotoPreview && (
              <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                <img 
                  src={newPhotoPreview} 
                  alt="Preview" 
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            {/* Caption */}
            <div>
              <Textarea
                placeholder="Scrivi una descrizione..."
                value={newPhotoCaption}
                onChange={(e) => setNewPhotoCaption(e.target.value)}
                className="min-h-[80px] resize-none"
                maxLength={500}
                data-testid="photo-caption-input"
              />
              <p className="text-xs text-gray-400 text-right mt-1">
                {newPhotoCaption.length}/500
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setShowUploadModal(false);
                  setNewPhotoFile(null);
                  setNewPhotoPreview(null);
                  setNewPhotoCaption("");
                }}
              >
                Annulla
              </Button>
              <Button 
                className="flex-1 bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 text-white"
                onClick={handlePhotoUpload}
                disabled={uploading}
                data-testid="publish-photo-btn"
              >
                {uploading ? "Pubblicando..." : "Pubblica"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Photo Detail Modal */}
      <Dialog open={!!selectedPhoto} onOpenChange={(open) => !open && setSelectedPhoto(null)}>
        <DialogContent className="sm:max-w-lg p-0 overflow-hidden max-h-[88dvh] flex flex-col gap-0 [&>button.absolute]:hidden" data-testid="post-detail-modal">
          {selectedPhoto && (
            <>
              {/* barra di chiusura sempre visibile */}
              <div className="flex items-center justify-between px-3 py-2 shrink-0 border-b border-white/10">
                <DialogTitle className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/45">Post</DialogTitle>
                <button onClick={() => setSelectedPhoto(null)} data-testid="post-detail-close"
                  className="p-2 -mr-1 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto overscroll-contain">
              {/* Canonical post view — same interactions as HERE */}
              {postDetail ? (
                <PostCard
                  post={postDetail}
                  formatTimeAgo={undefined}
                  onLike={detailLike}
                  onDoubleTapLike={(id) => !postDetail.is_liked && detailLike(id)}
                  onComment={detailComment}
                  onOpenLikes={(id) => setDetailLikes(id)}
                  onOpenGifts={(id) => setDetailGifts(id)}
                  onGiftSent={(id, amount, data) => {
                    setPostDetail((prev) => prev ? {
                      ...prev,
                      total_gifts: (prev.total_gifts || 0) + amount,
                      gift_value: (prev.gift_value || 0) + (data?.coins_spent || 0),
                    } : prev);
                  }}
                />
              ) : (
                <div className="relative aspect-square bg-black">
                  {selectedPhoto.media_type === 'video' ? (
                    <video
                      src={getImageUrl(selectedPhoto.image_url)}
                      className="w-full h-full object-contain"
                      controls
                      playsInline
                      autoPlay
                      muted
                      data-testid="photo-detail-video"
                    />
                  ) : (
                    <img 
                      src={getImageUrl(selectedPhoto.image_url)}
                      alt={selectedPhoto.caption || ""}
                      className="w-full h-full object-contain"
                    />
                  )}
                </div>
              )}
              <LikesListModal isOpen={!!detailLikes} onClose={() => setDetailLikes(null)} photoId={detailLikes} />
              <GiftsListModal isOpen={!!detailGifts} onClose={() => setDetailGifts(null)} photoId={detailGifts} />
              
              {/* Info */}
              <div className="p-4 pt-0">
                {/* Caption */}
                {editingCaption ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editedCaption}
                      onChange={(e) => setEditedCaption(e.target.value)}
                      className="min-h-[60px] resize-none"
                      maxLength={500}
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setEditingCaption(false);
                          setEditedCaption(selectedPhoto.caption || "");
                        }}
                      >
                        Annulla
                      </Button>
                      <Button 
                        size="sm"
                        onClick={handleUpdateCaption}
                        className="bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 text-white"
                      >
                        Salva
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-700">
                    {selectedPhoto.caption || <span className="text-gray-400 italic">{t("noDescription")}</span>}
                  </p>
                )}
                
                {/* Meta */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <span className="text-xs text-gray-400">
                    {formatDate(selectedPhoto.created_at)}
                  </span>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingCaption(true)}
                      className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
                      data-testid="edit-caption-btn"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePhoto(selectedPhoto.photo_id)}
                      className="p-2 rounded-full hover:bg-red-50 text-red-500"
                      data-testid="delete-photo-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Image Cropper Modal */}
      <ImageCropper
        open={showCropper}
        onClose={() => {
          setShowCropper(false);
          setCropperImage(null);
        }}
        imageSrc={cropperImage}
        onCropComplete={handleCroppedImage}
        aspectRatio={cropperType === 'avatar' ? 1 : cropperType === 'cover' ? 16/9 : 4/5}
        allowAspectChange={cropperType !== 'avatar' && cropperType !== 'cover'}
        title={
          cropperType === 'avatar' ? 'Ritaglia foto profilo' : 
          cropperType === 'cover' ? 'Ritaglia copertina' : 
          'Ritaglia foto'
        }
        cropShape={cropperType === 'avatar' ? 'round' : 'rect'}
      />
      
      {/* Avatar Zoom Modal */}
      <Dialog open={showAvatarZoom} onOpenChange={setShowAvatarZoom}>
        <DialogContent className="max-w-md p-0 bg-black/95 border-none overflow-hidden">
          <div className="relative flex items-center justify-center min-h-[400px]">
            {/* Close button */}
            <button
              onClick={() => {
                setShowAvatarZoom(false);
                setAvatarScale(1);
              }}
              className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Zoom controls */}
            <div className="absolute top-4 left-4 z-50 flex gap-2">
              <button
                onClick={() => setAvatarScale(prev => Math.max(prev - 0.5, 1))}
                disabled={avatarScale <= 1}
                className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors disabled:opacity-30"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="px-2 py-1.5 rounded-full bg-black/50 text-white text-xs">
                {Math.round(avatarScale * 100)}%
              </span>
              <button
                onClick={() => setAvatarScale(prev => Math.min(prev + 0.5, 4))}
                disabled={avatarScale >= 4}
                className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors disabled:opacity-30"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>
            
            {/* Avatar image */}
            {user?.picture && (
              <img
                src={getImageUrl(user.picture)}
                alt={user?.name}
                className="max-w-full max-h-[80vh] object-contain transition-transform duration-200"
                style={{ transform: `scale(${avatarScale})` }}
                onDoubleClick={() => {
                  if (avatarScale === 1) {
                    setAvatarScale(2);
                  } else {
                    setAvatarScale(1);
                  }
                }}
                data-testid="avatar-zoom-image"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Vetrina Livelli (solo profilo proprio) */}
      <LevelVault
        open={showLevelVault}
        onClose={() => setShowLevelVault(false)}
        isCreator={user?.creator_status === "approved"}
      />
    </div>
  );
}
